/** Extract a human-readable route short name from an opaque GTFS route_id. */
export function extractShortName(routeId: string | undefined): string | undefined {
  if (!routeId) return undefined;
  const compact = routeId.trim().toLocaleUpperCase();
  if (/^IWLR(?:[-_]|$)/.test(compact)) return 'L1';
  if (/^CSELR(?:[-_]|$)/.test(compact)) return 'L2';
  if (/^(?:IWLR|191)(?:[-_]|$)/.test(compact)) return 'L1';
  const compactModeCode = compact.match(/^\d*(?:L|F|T|M)\d+[A-Z]?$/)?.[0];
  if (compactModeCode) return compactModeCode.replace(/^\d+/, '');
  const prefixedModeCode = compact.match(/^\d+((?:L|F|T|M)\d+[A-Z]?)(?:[-_]|$)/)?.[1];
  if (prefixedModeCode) return prefixedModeCode;
  const trainPrefix: Record<string, string> = {
    // Sydney suburban lines — GTFS route_id prefixes → public line codes
    NTH: 'T9',   // Northern Line
    NSN: 'T1',   // North Shore Line
    WST: 'T1',   // Western Line
    ESI: 'T4',   // Eastern Suburbs & Illawarra Line
    IWL: 'T2',   // Inner West Line
    APS: 'T8',   // Airport Line
    OLY: 'T7',   // Olympic Park Line
    CMB: 'T5',   // Cumberland Line
    BMT: 'T1',   // Blue Mountains Line (runs on the T1 corridor within Sydney)
    // Regional/intercity lines — keep their recognised codes
    CCN: 'CCN',  // Central Coast & Newcastle Line
    SHL: 'SHL',  // Southern Highlands Line
    SCO: 'SCO',  // South Coast Line
    CTY: 'CTY',  // City Circle
    RTTA: 'RTTA',// Special services
  };
  const mappedTrain = trainPrefix[compact.split('_')[0]];
  if (mappedTrain) return mappedTrain;
  const railPrefix = compact.split('_')[0];
  const linePrefix = railPrefix.match(/^(?:T|L|F|M)\d+[A-Z]?/);
  if (linePrefix) return linePrefix[0];
  if (/^[A-Z]{2,4}$/.test(railPrefix)) return railPrefix;
  const humanLabelCode = routeId.trim().match(/^([A-Z]*\d+[A-Z]?)(?:\s|$)/)?.[1];
  if (humanLabelCode) return humanLabelCode;
  const prefixedEmbeddedLine = compact.match(/(?:^|[-_])\d+((?:T|L|F|M)\d+[A-Z]?)(?:$|[-_])/);
  if (prefixedEmbeddedLine) return prefixedEmbeddedLine[1];
  const embeddedLine = compact.match(/(?:^|[-_])((?:T|L|F|M)\d+[A-Z]?)(?:$|[-_])/);
  if (embeddedLine) return embeddedLine[1];
  // GTFS route_ids like "2459_389" → "389", "1-T1-sj2-1" → "T1", "B1_520" → "520"
  if (routeId.includes('_')) {
    const suffix = routeId.split('_').at(-1);
    if (suffix) return suffix;
  }
  const parts = routeId.split('-');
  const lineCode = parts.find(part => /^[TNFLM]\d+[A-Z]?$/.test(part));
  if (lineCode) return lineCode;
  const substantialNumber = [...parts].reverse().find(part => /^[A-Z]?\d{2,}[A-Z]?$/.test(part));
  return substantialNumber || parts.at(-1) || routeId;
}

/** Exact public route comparison; avoids route 52 matching 520/525/352. */
export function routeIdMatches(routeId: string | undefined, publicRoute: string): boolean {
  return extractShortName(routeId)?.toLocaleUpperCase() === extractShortName(publicRoute)?.toLocaleUpperCase();
}

/** Format a route label like "T1 to Bondi Junction" or "501 to City". */
export function formatRouteLabel(
  mode: string,
  routeShortName: string | undefined,
  destination: string | undefined,
): string {
  const dest = destination || '';
  const short = extractShortName(routeShortName);
  if (!short) {
    return mode === 'Metro' ? `Metro${dest ? ` to ${dest}` : ''}` : `${mode}${dest ? ` to ${dest}` : ''}`;
  }
  switch (mode) {
    case 'Train':
    case 'Bus':
    case 'Ferry':
    case 'Light Rail':
      return dest ? `${short} to ${dest}` : short;
    case 'Metro':
      return dest ? `Metro to ${dest}` : 'Metro';
    default:
      return dest ? `${short} to ${dest}` : short;
  }
}

/** Format delay seconds into a human-readable string. */
export function formatDelayText(seconds: number): string {
  const min = Math.round(seconds / 60);
  if (min > 0) return `${min} min late`;
  if (min < 0) return `${Math.abs(min)} min early`;
  return 'On time';
}
